import React from 'react';
import style from './giftCategory.module.scss';
import { LoyaltyGiftCategory } from 'acscp/store/helpers/giftsHelper';
import { Gift } from 'acscp/components/loyalty/gift';
import classNames from 'classnames';
import { Row } from 'acscp/components/common/row';
import debounce from 'lodash.debounce';
import { ArrowIcon } from 'acscp/components/common/icons/arrowIcon';

enum ScrollDirection {
    Left,
    Right,
}

interface GiftCategoryOwnProps {
    category: LoyaltyGiftCategory;
    isPlaceholder: boolean;
}

interface GiftCategoryState {
    isLeftScrollButtonEnabled: boolean;
    isRightScrollButtonEnabled: boolean;
    isAnimatingScroll: boolean;
}

export class GiftCategory extends React.Component<GiftCategoryOwnProps, GiftCategoryState> {
    private giftsSpriteWrapper: HTMLDivElement | null;

    constructor(props: GiftCategoryOwnProps) {
        super(props);
        this.state = {
            isLeftScrollButtonEnabled: false,
            isRightScrollButtonEnabled: false,
            isAnimatingScroll: false
        };
    }

    render(): JSX.Element | null {
        return (
            <Row className={style.GiftCategory}>
                <div className={classNames(style.GiftCategoryHeader, {[style.Placeholder]: this.props.isPlaceholder})}>
                    <span>{this.props.category.name}</span>
                </div>
                <div className={style.GiftCategoryGiftsContainer}>
                    <div
                        className={classNames(style.GiftCategoryGiftsLeft, {[style.Disabled]: !this.state.isLeftScrollButtonEnabled})}
                        onClick={this.scrollGiftsLeft}
                    >
                        <ArrowIcon/>
                    </div>
                    <div
                        className={style.GiftCategoryGifts}
                        ref={r => this.giftsSpriteWrapper = r}
                        onScroll={this.handleGiftsScroll}
                    >
                        {this.props.category.gifts.map((gift, index) => (
                            <div key={gift.id} className={style.GiftContainer}>
                                <Gift gift={gift} isPlaceholder={this.props.isPlaceholder}/>
                            </div>
                        ))}
                    </div>
                    <div
                        className={classNames(style.GiftCategoryGiftsRight, {[style.Disabled]: !this.state.isRightScrollButtonEnabled})}
                        onClick={this.scrollGiftsRight}
                    >
                        <ArrowIcon/>
                    </div>
                </div>
            </Row>
        );
    }

    componentDidMount(): void {
        window.addEventListener('resize', this.handleWindowResize);
        this.handleWindowResize();
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.handleWindowResize);
    }

    private scrollGiftsLeft: () => void = () => {
        this.scrollGiftsBy(-this.getScrollByDistance(ScrollDirection.Left));
    }

    private scrollGiftsRight: () => void = () => {
        this.scrollGiftsBy(this.getScrollByDistance(ScrollDirection.Right));
    }

    private scrollGiftsBy: (delta: number) => void = (delta: number) => {
        if (!this.giftsSpriteWrapper) {
            return;
        }

        this.animateScrollGiftsBy(delta)
            .then(this.updateScrollButtonStates);
    }

    private animateScrollGiftsBy: (scrollBy: number) => Promise<void> = (scrollBy: number) => {
        return new Promise<void>(resolve => {
            if (!this.giftsSpriteWrapper || this.state.isAnimatingScroll) {
                return resolve();
            }

            this.setState({isAnimatingScroll: true});
            const animationStartTime = Date.now();
            const duration = 300;
            const initialScrollLeft = this.giftsSpriteWrapper.scrollLeft;

            const completeAnimation = () => {
                this.setState({isAnimatingScroll: false});

                if (!!this.giftsSpriteWrapper) {
                    this.giftsSpriteWrapper.scrollLeft = initialScrollLeft + scrollBy;
                }

                resolve();
            };

            setTimeout(completeAnimation, duration); // ensure consistent state ready for next animation

            const animationFrame = () => {
                window.requestAnimationFrame(() => {
                    if (!this.giftsSpriteWrapper || !this.state.isAnimatingScroll) {
                        return completeAnimation();
                    }

                    const currentProgress = Math.min(1, (Date.now() - animationStartTime) / duration);
                    this.giftsSpriteWrapper.scrollLeft = initialScrollLeft + scrollBy * currentProgress;

                    if (currentProgress < 1) {
                        animationFrame();
                    } else {
                        completeAnimation();
                    }
                });
            };
            animationFrame();
        });
    }

    private getScrollByDistance: (scrollDirection: ScrollDirection) => number = scrollDirection => {
        if (!this.giftsSpriteWrapper) {
            return 0;
        }

        const giftWidth = this.giftsSpriteWrapper.clientWidth;
        const offsetFromCenter = this.giftsSpriteWrapper.scrollLeft % giftWidth;
        return offsetFromCenter > 0
            ? scrollDirection === ScrollDirection.Right
                ? giftWidth - offsetFromCenter
                : offsetFromCenter
            : giftWidth;
    }

    private updateScrollButtonStates: () => void = () => {
        if (!this.giftsSpriteWrapper) {
            return;
        }

        const minScrollLeft = 0;
        const maxScrollLeft = this.giftsSpriteWrapper.scrollWidth - this.giftsSpriteWrapper.clientWidth;
        const currentScrollLeft = this.giftsSpriteWrapper.scrollLeft;
        this.setState({
            isLeftScrollButtonEnabled: currentScrollLeft > minScrollLeft,
            isRightScrollButtonEnabled: currentScrollLeft < maxScrollLeft - 1 // - 1 corrects for rounding errors
        });
    }

    private handleGiftsScroll: () => void = debounce(this.updateScrollButtonStates, 150);
    private handleWindowResize: () => void = debounce(this.updateScrollButtonStates, 150);
}
